import React from "react";
import { motion } from "framer-motion";

/**
 * Loader — branded full-screen splash shown on first load.
 * A pulsing solar core with rotating rays over the hero's sky gradient.
 * Fades out via AnimatePresence when `loading` flips false in App.
 */
export default function Loader() {
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-gradient-to-b from-[#EAF3FB] via-[#FBF3E2] to-[#F6EEDD]"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
    >
      {/* Sun */}
      <div className="relative h-24 w-24">
        {/* Rotating rays */}
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
        >
          {Array.from({ length: 12 }).map((_, i) => (
            <span
              key={i}
              className="absolute left-1/2 top-0 h-3 w-1 rounded-full bg-accent-sun"
              style={{
                marginLeft: "-2px",
                transform: `rotate(${i * 30}deg)`,
                transformOrigin: "center 48px",
              }}
            />
          ))}
        </motion.div>

        {/* Pulsing core */}
        <motion.div
          className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-[0_0_45px_rgba(232,150,12,0.6)]"
          animate={{ scale: [1, 1.12, 1] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Wordmark */}
      <motion.div
        className="mt-9 font-display text-2xl font-bold tracking-[0.3em] text-[#0F2A4A]"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        SOLARES
      </motion.div>

      {/* Indeterminate progress sweep */}
      <div className="mt-5 h-1 w-40 overflow-hidden rounded-full bg-black/10">
        <motion.div
          className="h-full w-1/3 rounded-full bg-gradient-to-r from-amber-500 to-orange-600"
          animate={{ x: ["-110%", "330%"] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <motion.p
        className="mt-4 text-[11px] uppercase tracking-[0.25em] text-text-muted"
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      >
        Harnessing the sun…
      </motion.p>
    </motion.div>
  );
}
